/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/auth";
import { toast } from "sonner";
import {
  Users, Briefcase, Target, CheckSquare, BarChart3, Plus, Search, X, Trash2,
  Loader2, ArrowRight, TrendingUp, Phone, Mail, Building, Tag, Upload, Download,
  Calendar, Clock, AlertCircle, ChevronRight, Sparkles, MoreVertical,
} from "lucide-react";

const LEAD_STATUSES = [
  { key: "new", lbl: "New", color: "bg-sky-100 text-sky-800" },
  { key: "contacted", lbl: "Contacted", color: "bg-violet-100 text-violet-800" },
  { key: "qualified", lbl: "Qualified", color: "bg-emerald-100 text-emerald-800" },
  { key: "unqualified", lbl: "Unqualified", color: "bg-zinc-100 text-zinc-700" },
  { key: "converted", lbl: "Converted", color: "bg-[#1FA868] text-white" },
  { key: "lost", lbl: "Lost", color: "bg-red-100 text-red-800" },
];
const LEAD_SOURCES = ["website", "referral", "social", "ads", "event", "cold_call", "email_campaign", "marketplace", "other"];

const TABS = [
  { key: "dashboard", lbl: "Dashboard", icon: BarChart3 },
  { key: "leads",     lbl: "Leads",     icon: Target },
  { key: "deals",     lbl: "Pipeline",  icon: Briefcase },
  { key: "contacts",  lbl: "Contacts",  icon: Users },
  { key: "tasks",     lbl: "Tasks",     icon: CheckSquare },
];

const ngn = (n) => `₦${Number(n || 0).toLocaleString()}`;

export default function CRMPage() {
  const { subdomain } = useParams();
  const [tab, setTab] = useState("dashboard");
  const [planLocked, setPlanLocked] = useState(false);

  // Probe plan gating once on mount
  useEffect(() => {
    api.get("/t/crm/dashboard").catch((e) => {
      if (e?.response?.status === 403) setPlanLocked(true);
    });
  }, []);

  if (planLocked) {
    return (
      <div className="p-8" data-testid="crm-plan-locked">
        <div className="max-w-2xl mx-auto bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
          <Users size={32} className="mx-auto text-amber-700 mb-3" />
          <div className="font-display font-extrabold text-xl text-amber-900">CRM is not in your plan</div>
          <p className="mt-2 text-sm text-amber-800">Upgrade to manage leads, contacts and your full sales pipeline in one place.</p>
          <Link to={`/t/${subdomain}/billing`} className="inline-block mt-4 px-5 py-2.5 rounded-xl bg-[#1FA868] text-white font-bold text-sm">View plans</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="crm-page">
      {/* HEADER */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1B4332] to-[#1FA868] flex items-center justify-center text-white">
            <Users size={26} />
          </div>
          <div>
            <h1 className="font-display font-extrabold text-2xl lg:text-3xl text-black">CRM &amp; Sales Pipeline</h1>
            <p className="text-sm text-black/55 mt-1">Capture leads, nurture relationships, close more deals.</p>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="flex items-center gap-1 mb-6 border-b border-black/8" data-testid="crm-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`crm-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition ${
                active ? "border-[#1FA868] text-[#1FA868]" : "border-transparent text-black/55 hover:text-black"
              }`}>
              <Icon size={15} /> {t.lbl}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && <DashboardView />}
      {tab === "leads" && <LeadsView />}
      {tab === "deals" && <DealsView />}
      {tab === "contacts" && <ContactsView />}
      {tab === "tasks" && <TasksView />}
    </div>
  );
}

// ───────────────────────── DASHBOARD ─────────────────────────
function DashboardView() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/t/crm/dashboard").then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data) return <Spinner />;
  const k = data.kpis;
  const totalSourced = Object.values(data.sources || {}).reduce((s, n) => s + n, 0);

  return (
    <div data-testid="crm-dashboard">
      {/* KPI Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPICard title="Total Leads" value={k.total_leads} icon={Target} accent="bg-sky-50 text-sky-700" testid="kpi-leads" />
        <KPICard title="Open Deals" value={k.open_deals_count} icon={Briefcase} accent="bg-violet-50 text-violet-700" testid="kpi-open-deals" />
        <KPICard title="Pipeline Value" value={ngn(k.total_pipeline_value)} icon={TrendingUp} accent="bg-emerald-50 text-emerald-700" testid="kpi-pipeline" />
        <KPICard title="Won Value" value={ngn(k.won_value)} icon={Sparkles} accent="bg-amber-50 text-amber-700" testid="kpi-won" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <KPICard title="Weighted Pipeline" value={ngn(k.weighted_value)} hint="Probability × value" testid="kpi-weighted" />
        <KPICard title="Lead → Deal Conversion" value={`${k.conversion_rate}%`} hint="Converted ÷ Total leads" testid="kpi-conversion" />
        <KPICard title="Win Rate" value={`${k.win_rate}%`} hint="Won ÷ Closed deals" testid="kpi-winrate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Lead funnel */}
        <div className="bg-white rounded-2xl border border-black/8 p-5">
          <div className="font-display font-extrabold text-lg text-black mb-4">Lead Funnel</div>
          <div className="space-y-3">
            {LEAD_STATUSES.map((s) => {
              const count = data.funnel?.[s.key] || 0;
              const max = Math.max(1, ...Object.values(data.funnel || {}));
              return (
                <div key={s.key}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-bold text-black/75">{s.lbl}</span>
                    <span className="font-bold tabular-nums">{count}</span>
                  </div>
                  <div className="h-2.5 bg-black/5 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#1B4332] to-[#1FA868] rounded-full transition-all" style={{ width: `${(count / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Source attribution */}
        <div className="bg-white rounded-2xl border border-black/8 p-5">
          <div className="font-display font-extrabold text-lg text-black mb-4">Lead Source Attribution</div>
          {totalSourced === 0 ? (
            <div className="text-sm text-black/45 text-center py-6">No leads yet — create your first lead in the Leads tab.</div>
          ) : (
            <div className="space-y-2">
              {Object.entries(data.sources).sort((a, b) => b[1] - a[1]).map(([src, count]) => (
                <div key={src} className="flex items-center gap-3 text-sm">
                  <span className="capitalize font-semibold text-black/85 w-32 flex-shrink-0">{src.replace("_", " ")}</span>
                  <div className="flex-1 h-2 bg-black/5 rounded-full">
                    <div className="h-full bg-[#1FA868] rounded-full" style={{ width: `${(count / totalSourced) * 100}%` }} />
                  </div>
                  <span className="font-bold tabular-nums w-10 text-right">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KPICard({ title, value, icon: Icon, accent, hint, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-black/8 p-4 lg:p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wide font-bold text-black/55">{title}</div>
          <div className="mt-1 font-display font-extrabold text-2xl text-black">{value}</div>
          {hint && <div className="text-[11px] text-black/45 mt-1">{hint}</div>}
        </div>
        {Icon && <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent || "bg-black/5 text-black/55"}`}><Icon size={18} /></div>}
      </div>
    </div>
  );
}

// ───────────────────────── LEADS ─────────────────────────
function LeadsView() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState("all");
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [editLead, setEditLead] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusF !== "all") params.set("status", statusF);
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(`/t/crm/leads?${params}`);
      setLeads(r.data || []);
    } catch { setLeads([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusF]);

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await api.delete(`/t/crm/leads/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const convert = async (id) => {
    if (!window.confirm("Convert this lead to a Contact + Deal?")) return;
    try { const r = await api.post(`/t/crm/leads/${id}/convert`); toast.success("Converted! Deal created."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const exportCsv = async () => {
    try {
      const r = await api.get("/t/crm/leads-export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = "leads.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const importCsv = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/t/crm/leads-import", fd); toast.success(`Imported ${r.data.added} leads`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
  };

  return (
    <div data-testid="crm-leads-view">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name, email, company…"
            className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-black/15 text-sm focus:border-[#1FA868] outline-none"
            data-testid="crm-leads-search" />
        </div>
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="crm-leads-status-filter"
          className="px-3 py-2.5 rounded-lg border border-black/15 text-sm font-semibold">
          <option value="all">All statuses</option>
          {LEAD_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.lbl}</option>)}
        </select>
        <label className="px-3 py-2.5 rounded-lg bg-black/5 text-sm font-bold cursor-pointer flex items-center gap-2" data-testid="crm-leads-import">
          <Upload size={14} /> Import
          <input type="file" accept=".csv" className="hidden" onChange={(e) => importCsv(e.target.files?.[0])} />
        </label>
        <button onClick={exportCsv} className="px-3 py-2.5 rounded-lg bg-black/5 text-sm font-bold flex items-center gap-2" data-testid="crm-leads-export"><Download size={14} /> Export</button>
        <button onClick={() => setShowNew(true)} className="px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold flex items-center gap-2" data-testid="crm-leads-new-btn"><Plus size={14} /> New Lead</button>
      </div>

      <div className="bg-white rounded-2xl border border-black/8 overflow-hidden">
        {loading ? <Spinner /> : leads.length === 0 ? (
          <div className="p-12 text-center" data-testid="crm-leads-empty">
            <Target size={32} className="mx-auto text-black/25 mb-3" />
            <div className="font-bold text-black/70">No leads yet</div>
            <p className="text-sm text-black/45 mt-1">Add your first lead to start building the pipeline.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide text-black/55 font-bold">
              <tr>
                <Th>Name</Th><Th>Company</Th><Th>Email / Phone</Th><Th>Source</Th><Th>Status</Th><Th right>Value</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => {
                const status = LEAD_STATUSES.find((s) => s.key === l.status) || LEAD_STATUSES[0];
                return (
                  <tr key={l.id} className="border-t border-black/5 hover:bg-black/[0.015]" data-testid={`crm-lead-row-${l.id}`}>
                    <Td><div className="font-bold text-black">{l.name}</div>{l.title && <div className="text-[11px] text-black/55">{l.title}</div>}</Td>
                    <Td>{l.company || "—"}</Td>
                    <Td>
                      {l.email && <div className="flex items-center gap-1.5 text-xs"><Mail size={11} className="text-black/45" />{l.email}</div>}
                      {l.phone && <div className="flex items-center gap-1.5 text-xs mt-0.5"><Phone size={11} className="text-black/45" />{l.phone}</div>}
                    </Td>
                    <Td className="text-xs capitalize">{(l.source || "").replace("_", " ")}</Td>
                    <Td><span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${status.color}`}>{status.lbl}</span></Td>
                    <Td right className="font-bold">{ngn(l.value)}</Td>
                    <Td>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setEditLead(l)} className="px-2 py-1 text-xs font-bold text-[#1FA868] hover:bg-[#1FA868]/10 rounded" data-testid={`crm-lead-edit-${l.id}`}>Edit</button>
                        {l.status !== "converted" && (
                          <button onClick={() => convert(l.id)} className="px-2 py-1 text-xs font-bold text-violet-700 hover:bg-violet-50 rounded" data-testid={`crm-lead-convert-${l.id}`}>Convert</button>
                        )}
                        <button onClick={() => remove(l.id)} className="p-1 text-red-600 hover:bg-red-50 rounded" data-testid={`crm-lead-delete-${l.id}`}><Trash2 size={13} /></button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {(showNew || editLead) && <LeadEditor lead={editLead} onClose={() => { setShowNew(false); setEditLead(null); }} onSaved={() => { setShowNew(false); setEditLead(null); load(); }} />}
    </div>
  );
}

function LeadEditor({ lead, onClose, onSaved }) {
  const isEdit = !!lead;
  const [f, setF] = useState({
    name: lead?.name || "", email: lead?.email || "", phone: lead?.phone || "",
    company: lead?.company || "", title: lead?.title || "",
    source: lead?.source || "website", status: lead?.status || "new",
    value: lead?.value || 0, notes: lead?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/t/crm/leads/${lead.id}`, f);
      else await api.post("/t/crm/leads", f);
      toast.success(isEdit ? "Updated" : "Lead created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? "Edit Lead" : "New Lead"} onClose={onClose} testid="crm-lead-editor">
      <div className="grid grid-cols-2 gap-3">
        <Field lbl="Name *" full><Input value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="lead-name" /></Field>
        <Field lbl="Title"><Input value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="lead-title" /></Field>
        <Field lbl="Company"><Input value={f.company} onChange={(v) => setF({ ...f, company: v })} testid="lead-company" /></Field>
        <Field lbl="Email"><Input type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="lead-email" /></Field>
        <Field lbl="Phone"><Input value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="lead-phone" /></Field>
        <Field lbl="Source">
          <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="lead-source">
            {LEAD_SOURCES.map((s) => <option key={s} value={s} className="capitalize">{s.replace("_", " ")}</option>)}
          </select>
        </Field>
        <Field lbl="Status">
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="lead-status">
            {LEAD_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.lbl}</option>)}
          </select>
        </Field>
        <Field lbl="Estimated Value (₦)"><Input type="number" value={f.value} onChange={(v) => setF({ ...f, value: parseFloat(v) || 0 })} testid="lead-value" /></Field>
        <Field lbl="Notes" full><textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="lead-notes" /></Field>
      </div>
      <ModalFooter onCancel={onClose} onSave={submit} saving={saving} testid="lead-save" />
    </Modal>
  );
}

// ───────────────────────── DEALS (KANBAN) ─────────────────────────
function DealsView() {
  const [pipelines, setPipelines] = useState([]);
  const [activePipe, setActivePipe] = useState("default");
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editDeal, setEditDeal] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, d] = await Promise.all([
        api.get("/t/crm/pipelines"),
        api.get(`/t/crm/deals?pipeline_id=${activePipe}`),
      ]);
      setPipelines(p.data || []);
      setDeals(d.data || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activePipe]);

  const pipeline = useMemo(() => pipelines.find((p) => p.id === activePipe) || pipelines[0], [pipelines, activePipe]);
  const stages = pipeline?.stages || [];

  const moveDeal = async (dealId, newStage) => {
    setDeals((prev) => prev.map((d) => (d.id === dealId ? { ...d, stage: newStage } : d)));
    try { await api.patch(`/t/crm/deals/${dealId}`, { stage: newStage }); toast.success("Moved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); load(); }
  };
  const removeDeal = async (id) => {
    if (!window.confirm("Delete this deal?")) return;
    try { await api.delete(`/t/crm/deals/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading && !pipeline) return <Spinner />;

  return (
    <div data-testid="crm-deals-view">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {pipelines.length > 1 && (
            <select value={activePipe} onChange={(e) => setActivePipe(e.target.value)} className="px-3 py-2 rounded-lg border border-black/15 text-sm font-semibold" data-testid="crm-pipeline-picker">
              {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <div className="text-sm text-black/55">{deals.length} deals · Total {ngn(deals.reduce((s, d) => s + (d.value || 0), 0))}</div>
        </div>
        <button onClick={() => setShowNew(true)} className="px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold flex items-center gap-2" data-testid="crm-deal-new-btn"><Plus size={14} /> New Deal</button>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2">
        {stages.map((s) => {
          const stageDeals = deals.filter((d) => d.stage === s.key);
          const stageTotal = stageDeals.reduce((sum, d) => sum + (d.value || 0), 0);
          return (
            <div key={s.key}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragId) moveDeal(dragId, s.key); setDragId(null); }}
              className="flex-shrink-0 w-72 bg-black/[0.025] rounded-xl p-3"
              data-testid={`crm-stage-${s.key}`}>
              <div className="flex items-center gap-2 mb-3 px-1">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                <div className="font-bold text-sm text-black flex-1">{s.name}</div>
                <span className="text-[11px] text-black/55 font-bold">{stageDeals.length} · {ngn(stageTotal)}</span>
              </div>
              <div className="space-y-2">
                {stageDeals.map((d) => (
                  <div key={d.id} draggable onDragStart={() => setDragId(d.id)} onDragEnd={() => setDragId(null)}
                    onClick={() => setEditDeal(d)}
                    data-testid={`crm-deal-card-${d.id}`}
                    className="bg-white rounded-lg border border-black/8 p-3 cursor-grab active:cursor-grabbing hover:border-[#1FA868] hover:shadow-md transition">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="font-bold text-sm text-black flex-1 truncate">{d.title}</div>
                      <button onClick={(e) => { e.stopPropagation(); removeDeal(d.id); }} className="text-black/35 hover:text-red-600 flex-shrink-0"><Trash2 size={12} /></button>
                    </div>
                    <div className="font-display font-extrabold text-[#1FA868] text-base">{ngn(d.value)}</div>
                    <div className="flex items-center gap-1.5 mt-2 text-[10px] text-black/55">
                      <span className="bg-black/5 px-1.5 py-0.5 rounded font-bold">{d.probability ?? 0}%</span>
                      {d.expected_close_date && <span className="flex items-center gap-1"><Calendar size={9} />{d.expected_close_date.slice(0, 10)}</span>}
                    </div>
                  </div>
                ))}
                {stageDeals.length === 0 && <div className="text-center text-xs text-black/30 py-4">Drop deals here</div>}
              </div>
            </div>
          );
        })}
      </div>

      {(showNew || editDeal) && <DealEditor deal={editDeal} pipeline={pipeline} onClose={() => { setShowNew(false); setEditDeal(null); }} onSaved={() => { setShowNew(false); setEditDeal(null); load(); }} />}
    </div>
  );
}

function DealEditor({ deal, pipeline, onClose, onSaved }) {
  const isEdit = !!deal;
  const [f, setF] = useState({
    title: deal?.title || "",
    stage: deal?.stage || pipeline?.stages?.[0]?.key || "discovery",
    value: deal?.value || 0,
    probability: deal?.probability ?? "",
    expected_close_date: deal?.expected_close_date?.slice(0, 10) || "",
    notes: deal?.notes || "",
    pipeline_id: pipeline?.id || "default",
  });
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState(deal?.contact_id || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/t/crm/contacts").then((r) => setContacts(r.data || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!f.title.trim()) return toast.error("Deal title required");
    setSaving(true);
    try {
      const payload = { ...f, contact_id: contactId || null, value: parseFloat(f.value) || 0,
                        probability: f.probability === "" ? undefined : parseInt(f.probability, 10) };
      if (isEdit) await api.patch(`/t/crm/deals/${deal.id}`, payload);
      else await api.post("/t/crm/deals", payload);
      toast.success(isEdit ? "Updated" : "Deal created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? "Edit Deal" : "New Deal"} onClose={onClose} testid="crm-deal-editor">
      <div className="grid grid-cols-2 gap-3">
        <Field lbl="Title *" full><Input value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="deal-title" /></Field>
        <Field lbl="Stage">
          <select value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="deal-stage">
            {(pipeline?.stages || []).map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
        </Field>
        <Field lbl="Value (₦)"><Input type="number" value={f.value} onChange={(v) => setF({ ...f, value: v })} testid="deal-value" /></Field>
        <Field lbl="Probability %"><Input type="number" value={f.probability} onChange={(v) => setF({ ...f, probability: v })} placeholder="Auto from stage" testid="deal-probability" /></Field>
        <Field lbl="Expected close"><Input type="date" value={f.expected_close_date} onChange={(v) => setF({ ...f, expected_close_date: v })} testid="deal-close-date" /></Field>
        <Field lbl="Contact" full>
          <select value={contactId} onChange={(e) => setContactId(e.target.value)} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="deal-contact">
            <option value="">— None —</option>
            {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ""}</option>)}
          </select>
        </Field>
        <Field lbl="Notes" full><textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="deal-notes" /></Field>
      </div>
      <ModalFooter onCancel={onClose} onSave={submit} saving={saving} testid="deal-save" />
    </Modal>
  );
}

// ───────────────────────── CONTACTS ─────────────────────────
function ContactsView() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [editContact, setEditContact] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(`/t/crm/contacts?${params}`);
      setContacts(r.data || []);
    } catch { setContacts([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try { await api.delete(`/t/crm/contacts/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="crm-contacts-view">
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search contacts" className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-black/15 text-sm" data-testid="crm-contacts-search" />
        </div>
        <button onClick={() => setShowNew(true)} className="px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold flex items-center gap-2" data-testid="crm-contact-new-btn"><Plus size={14} /> New Contact</button>
      </div>

      <div className="bg-white rounded-2xl border border-black/8 overflow-hidden">
        {loading ? <Spinner /> : contacts.length === 0 ? (
          <div className="p-12 text-center" data-testid="crm-contacts-empty">
            <Users size={32} className="mx-auto text-black/25 mb-3" />
            <div className="font-bold text-black/70">No contacts yet</div>
          </div>
        ) : (
          <div className="divide-y divide-black/5">
            {contacts.map((c) => (
              <div key={c.id} className="p-4 flex items-center gap-3 hover:bg-black/[0.015]" data-testid={`crm-contact-row-${c.id}`}>
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1B4332] to-[#1FA868] text-white flex items-center justify-center font-bold text-sm">{(c.name || "?").slice(0, 2).toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-black">{c.name}</div>
                  <div className="text-xs text-black/55 flex items-center gap-3 mt-0.5 flex-wrap">
                    {c.email && <span className="flex items-center gap-1"><Mail size={10} />{c.email}</span>}
                    {c.phone && <span className="flex items-center gap-1"><Phone size={10} />{c.phone}</span>}
                    {c.company && <span className="flex items-center gap-1"><Building size={10} />{c.company}</span>}
                  </div>
                </div>
                <button onClick={() => setEditContact(c)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-[#1FA868] hover:bg-[#1FA868]/10" data-testid={`crm-contact-edit-${c.id}`}>Edit</button>
                <button onClick={() => remove(c.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" data-testid={`crm-contact-delete-${c.id}`}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {(showNew || editContact) && <ContactEditor contact={editContact} onClose={() => { setShowNew(false); setEditContact(null); }} onSaved={() => { setShowNew(false); setEditContact(null); load(); }} />}
    </div>
  );
}

function ContactEditor({ contact, onClose, onSaved }) {
  const isEdit = !!contact;
  const [f, setF] = useState({
    name: contact?.name || "", email: contact?.email || "", phone: contact?.phone || "",
    company: contact?.company || "", title: contact?.title || "", address: contact?.address || "", notes: contact?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/t/crm/contacts/${contact.id}`, f);
      else await api.post("/t/crm/contacts", f);
      toast.success(isEdit ? "Updated" : "Contact created"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={isEdit ? "Edit Contact" : "New Contact"} onClose={onClose} testid="crm-contact-editor">
      <div className="grid grid-cols-2 gap-3">
        <Field lbl="Name *" full><Input value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="contact-name" /></Field>
        <Field lbl="Email"><Input type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="contact-email" /></Field>
        <Field lbl="Phone"><Input value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="contact-phone" /></Field>
        <Field lbl="Company"><Input value={f.company} onChange={(v) => setF({ ...f, company: v })} testid="contact-company" /></Field>
        <Field lbl="Title"><Input value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="contact-title" /></Field>
        <Field lbl="Address" full><Input value={f.address} onChange={(v) => setF({ ...f, address: v })} testid="contact-address" /></Field>
        <Field lbl="Notes" full><textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
      </div>
      <ModalFooter onCancel={onClose} onSave={submit} saving={saving} testid="contact-save" />
    </Modal>
  );
}

// ───────────────────────── TASKS ─────────────────────────
function TasksView() {
  const [tasks, setTasks] = useState([]);
  const [statusF, setStatusF] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusF !== "all") params.set("status", statusF);
      const r = await api.get(`/t/crm/tasks?${params}`);
      setTasks(r.data || []);
    } catch { setTasks([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusF]);

  const toggle = async (t) => {
    const next = t.status === "done" ? "pending" : "done";
    try { await api.patch(`/t/crm/tasks/${t.id}`, { status: next }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try { await api.delete(`/t/crm/tasks/${id}`); load(); } catch { /* noop */ }
  };

  return (
    <div data-testid="crm-tasks-view">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-1 bg-black/[0.04] rounded-lg p-1">
          {["all", "pending", "in_progress", "done"].map((s) => (
            <button key={s} onClick={() => setStatusF(s)} data-testid={`crm-tasks-filter-${s}`}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition ${statusF === s ? "bg-white text-black shadow-sm" : "text-black/55"}`}>
              {s === "all" ? "All" : s === "in_progress" ? "In Progress" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <button onClick={() => setShowNew(true)} className="px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold flex items-center gap-2" data-testid="crm-task-new-btn"><Plus size={14} /> New Task</button>
      </div>

      <div className="bg-white rounded-2xl border border-black/8 overflow-hidden">
        {loading ? <Spinner /> : tasks.length === 0 ? (
          <div className="p-12 text-center" data-testid="crm-tasks-empty">
            <CheckSquare size={32} className="mx-auto text-black/25 mb-3" />
            <div className="font-bold text-black/70">No tasks yet</div>
          </div>
        ) : (
          <div className="divide-y divide-black/5">
            {tasks.map((t) => {
              const overdue = t.due_date && t.status !== "done" && new Date(t.due_date) < new Date();
              return (
                <div key={t.id} className="p-4 flex items-center gap-3 hover:bg-black/[0.015]" data-testid={`crm-task-row-${t.id}`}>
                  <button onClick={() => toggle(t)} className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${t.status === "done" ? "bg-[#1FA868] border-[#1FA868]" : "border-black/25 hover:border-[#1FA868]"}`} data-testid={`crm-task-toggle-${t.id}`}>
                    {t.status === "done" && <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M5 12l5 5L20 7" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`font-bold text-sm ${t.status === "done" ? "text-black/40 line-through" : "text-black"}`}>{t.title}</div>
                    <div className="flex items-center gap-3 mt-0.5 text-[11px] text-black/55">
                      <span className={`px-1.5 py-0.5 rounded font-bold uppercase ${
                        t.priority === "urgent" ? "bg-red-100 text-red-700" :
                        t.priority === "high" ? "bg-amber-100 text-amber-700" : "bg-black/5 text-black/55"}`}>{t.priority}</span>
                      {t.due_date && <span className={`flex items-center gap-1 ${overdue ? "text-red-600 font-bold" : ""}`}>
                        {overdue ? <AlertCircle size={11} /> : <Clock size={11} />}
                        {t.due_date.slice(0, 10)}
                      </span>}
                      {t.related_to_type && <span className="capitalize">→ {t.related_to_type}</span>}
                    </div>
                  </div>
                  <button onClick={() => remove(t.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" data-testid={`crm-task-delete-${t.id}`}><Trash2 size={13} /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showNew && <TaskEditor onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function TaskEditor({ onClose, onSaved }) {
  const [f, setF] = useState({ title: "", description: "", due_date: "", priority: "normal", status: "pending" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    setSaving(true);
    try { await api.post("/t/crm/tasks", f); toast.success("Task created"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="New Task" onClose={onClose} testid="crm-task-editor">
      <div className="grid grid-cols-2 gap-3">
        <Field lbl="Title *" full><Input value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="task-title" /></Field>
        <Field lbl="Due date"><Input type="date" value={f.due_date} onChange={(v) => setF({ ...f, due_date: v })} testid="task-due" /></Field>
        <Field lbl="Priority">
          <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="task-priority">
            <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
          </select>
        </Field>
        <Field lbl="Description" full><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="task-desc" /></Field>
      </div>
      <ModalFooter onCancel={onClose} onSave={submit} saving={saving} testid="task-save" />
    </Modal>
  );
}

// ───────────────────────── SHARED UI ─────────────────────────
function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-black/8 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="font-display font-extrabold text-lg text-black">{title}</div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5"><X size={16} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
function ModalFooter({ onCancel, onSave, saving, testid }) {
  return (
    <div className="flex gap-2 pt-4 mt-4 border-t border-black/8">
      <button onClick={onCancel} className="flex-1 px-4 py-2.5 rounded-lg bg-black/5 text-sm font-bold text-black/70">Cancel</button>
      <button onClick={onSave} disabled={saving} className="flex-1 px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold disabled:opacity-50" data-testid={testid}>
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}
function Field({ lbl, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-[11px] uppercase tracking-wide font-bold text-black/55">{lbl}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function Input({ value, onChange, type = "text", placeholder, testid }) {
  return <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid={testid} />;
}
function Th({ children, right }) {
  return <th className={`px-4 py-2.5 ${right ? "text-right" : "text-left"}`}>{children}</th>;
}
function Td({ children, right, className = "" }) {
  return <td className={`px-4 py-3 ${right ? "text-right" : ""} ${className}`}>{children}</td>;
}
function Spinner() {
  return <div className="p-12 flex items-center justify-center text-black/45"><Loader2 className="animate-spin" /></div>;
}
